import * as React from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '../../lib/utils.js';

export interface SelectProps
  extends React.SelectHTMLAttributes<HTMLSelectElement> {
  options: Array<{ value: string; label: string }>;
  placeholder?: string;
}

const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  ({ className, options, placeholder, ...props }, ref) => {
    return (
      <div className="relative">
        <select
          className={cn(
            'flex h-10 w-full appearance-none rounded-lg px-3 py-2 pr-9 text-sm',
            'border border-[var(--color-border)] bg-[var(--color-input)] text-[var(--color-foreground)]',
            'shadow-[0_1px_2px_rgba(0,0,0,0.1)]',
            'transition-all duration-200',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-background)]',
            'focus-visible:border-[var(--color-ring)]',
            'hover:border-[var(--color-muted-foreground)]/50',
            'disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-[var(--color-border)]',
            className
          )}
          ref={ref}
          {...props}
        >
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--color-muted-foreground)]" />
      </div>
    );
  }
);
Select.displayName = 'Select';

export interface MultiSelectProps
  extends Omit<React.InputHTMLAttributes<HTMLDivElement>, 'onChange'> {
  options: Array<{ value: string; label: string }>;
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
}

const MultiSelect = React.forwardRef<HTMLDivElement, MultiSelectProps>(
  ({ className, options, value, onChange, placeholder, ...props }, ref) => {
    const [isOpen, setIsOpen] = React.useState(false);

    const toggleOption = (optionValue: string) => {
      if (value.includes(optionValue)) {
        onChange(value.filter((v) => v !== optionValue));
      } else {
        onChange([...value, optionValue]);
      }
    };

    return (
      <div className="relative" ref={ref} {...props}>
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className={cn(
            'flex h-10 w-full items-center justify-between rounded-lg px-3 py-2 text-sm',
            'border border-[var(--color-border)] bg-[var(--color-input)] text-[var(--color-foreground)]',
            'shadow-[0_1px_2px_rgba(0,0,0,0.1)]',
            'transition-all duration-200',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-background)]',
            'hover:border-[var(--color-muted-foreground)]/50',
            'disabled:cursor-not-allowed disabled:opacity-50',
            className
          )}
        >
          <span className="truncate text-[var(--color-muted-foreground)]">
            {value.length > 0
              ? options
                  .filter((o) => value.includes(o.value))
                  .map((o) => o.label)
                  .join(', ')
              : placeholder || 'Select...'}
          </span>
          <ChevronDown className="h-4 w-4 text-[var(--color-muted-foreground)]" />
        </button>
        {isOpen && (
          <div
            className={cn(
              'absolute z-50 mt-1 w-full rounded-lg py-1',
              'border border-[var(--color-border)] bg-[var(--color-card)]',
              'shadow-[0_4px_12px_rgba(0,0,0,0.3)]',
              'animate-[scale-in_0.1s_ease-out]'
            )}
          >
            {options.map((option) => (
              <label
                key={option.value}
                className={cn(
                  'flex cursor-pointer items-center gap-2 px-3 py-2',
                  'transition-colors duration-150',
                  'hover:bg-[var(--color-muted)]/50'
                )}
              >
                <input
                  type="checkbox"
                  checked={value.includes(option.value)}
                  onChange={() => toggleOption(option.value)}
                  className="h-4 w-4 rounded border-[var(--color-border)] accent-[var(--color-primary)]"
                />
                <span className="text-sm text-[var(--color-foreground)]">{option.label}</span>
              </label>
            ))}
          </div>
        )}
      </div>
    );
  }
);
MultiSelect.displayName = 'MultiSelect';

export { Select, MultiSelect };
